import { TRAINING_GATE } from '@routes/userRoutes/objects/constants';
import {
	getTrainingGate,
	formatCount,
} from '@routes/userRoutes/objects/components/objectDetails/objectsUtils';
import {
	describeLabelMissing,
	labelsFromSummary,
	pluralize,
	sampleUnitOf,
	trainingSampleFor,
} from '@routes/userRoutes/objects/objectTree';

/** Which Train New Object modal a node gets; `null` for none (no counts to judge by). */
// TODO: the design's "Ready to train or create object" (retrain a custom object, or create
// one under it) waits for the node train endpoint; until then a ready node can only seed one.
export const TRAIN_MODAL = Object.freeze({ BLOCKED: 'blocked', READY: 'ready' });

// The modal for a training gate; `null` while the gate is UNKNOWN.
function kindOfGate({ status }) {
	if (status === TRAINING_GATE.READY) return TRAIN_MODAL.READY;
	if (status === TRAINING_GATE.LOCKED) return TRAIN_MODAL.BLOCKED;
	return null;
}

const countOf = (count, word) => `${formatCount(count)} ${pluralize(count, word)}`;

// "240/10", met or not.
const check = (key, label, { count, required, met }) => ({
	key,
	label,
	value: `${formatCount(count)}/${formatCount(required)}`,
	met,
});

// "10 annotations on 6 checked tiles": the server's threshold, per object or label.
const minimumOf = (gate, unit) =>
	gate.tiles
		? `${countOf(gate.annotations.required, 'annotation')} on ${countOf(gate.tiles.required, `checked ${unit}`)}`
		: countOf(gate.annotations.required, 'annotation');

// One row per label the node's examples are split by, Default first when it has any. A
// label that cannot train says what it is short of (`missing`), for the "not yet" tag.
const labelRowsOf = (node, unit, projectMode) => {
	const labels = labelsFromSummary(node?.annotation_labels);
	if (!labels.labelOptions.length) return [];
	const keys = labels.labelCounts[''] > 0 ? ['', ...labels.labelOptions] : labels.labelOptions;
	return keys.map(key => {
		const { annotations, tiles } = trainingSampleFor(node, key, labels);
		const canTrain = !labels.untrainable.has(key);
		return {
			key,
			label: key || 'Default (no label)',
			counts: `${countOf(annotations, 'annotation')} · ${countOf(tiles, unit)}`,
			canTrain,
			missing: canTrain
				? null
				: describeLabelMissing(node, { annotations, tiles }, projectMode),
		};
	});
};

/**
 * What the Train New Object modal says for `node`, the active annotation object, from
 * the annotation summary merged into it: the title, the body, the checks against the
 * server's thresholds (blocked only) and the per-label counts. The counts are the
 * summary's, which span every project, so nothing here says "in this project".
 *
 * Blocked shows "New since last training" only when the counts are met: that is when
 * the gate can tell it is the reason. With counts short, the summary does not say.
 * Ready names the minimum too, when the server sent one, so a "not yet" label reads
 * against it.
 *
 * @param {object} node
 * @param {string} [projectMode] - a `PROJECT_MODE` value: names tiles as tiles or images
 * @returns {{ kind: string, title: string, body: string,
 *   checks: Array<{ key: string, label: string, value: string, met: boolean }>,
 *   labelRows: Array<{ key: string, label: string, counts: string, canTrain: boolean,
 *     missing: string|null }> } | null}
 */
export const trainModalContent = (node, projectMode) => {
	const gate = getTrainingGate(node);
	const kind = kindOfGate(gate);
	if (!kind) return null;

	const unit = sampleUnitOf(projectMode);
	const units = pluralize(2, unit);
	const name = node.name ?? 'this object';
	const labelRows = labelRowsOf(node, unit, projectMode);

	if (kind === TRAIN_MODAL.READY) {
		const tiles = gate.tiles ? `${countOf(gate.tiles.count, `checked ${unit}`)} and ` : '';
		// The server can clear a node to train without sending thresholds, which leaves
		// the gate without requirements; the count itself is the node's either way.
		// TODO: the summary's node total has matched the Default label alone, not the sum of the
		// label rows below it (72 vs 97); kept as sent until the backend says which is right.
		const annotations = gate.annotations?.count ?? node.annotations ?? 0;
		const verb = !tiles && annotations === 1 ? 'is' : 'are';
		// Without thresholds the server cleared the node on its own terms: no minimum to name.
		const minimum = gate.annotations
			? ` Training needs at least ${minimumOf(gate, unit)} for one object or label.`
			: '';
		return {
			kind,
			title: 'Create a new object from this work?',
			body: `${tiles}${countOf(annotations, 'annotation')} ${verb} ready. Use them to train a new object under “${name}”, or just save them to the project for later.${minimum}`,
			checks: [],
			labelRows,
		};
	}

	const minimum = minimumOf(gate, unit);
	const body = gate.needsNewAnnotations
		? `Nothing new since “${name}” was last trained. Annotate more ${units} and come back.`
		: `Training needs at least ${minimum} for one object or label. Empty ${units} count as checked. Save what you have and continue any time.`;

	return {
		kind,
		title: 'Not enough training data yet',
		body,
		checks: [
			check('annotations', `Annotations for ${name}`, gate.annotations),
			gate.tiles && check('tiles', `Checked ${units}`, gate.tiles),
			gate.needsNewAnnotations && {
				key: 'fresh',
				label: 'New since last training',
				value: 'no',
				met: false,
			},
		].filter(Boolean),
		labelRows,
	};
};
